import React, { createContext, useContext, useState, useEffect, ReactNode, useMemo } from 'react';
import { jerseyApi } from '../api/jerseyApi';

interface User {
  id: number;
  email: string;
  full_name?: string | null;
  is_superuser: boolean;
}

type AuthMode = 'local' | 'cognito';

interface AuthContextType {
  user: User | null;
  token: string | null;
  authMode: AuthMode;
  login: (email: string, password: string) => Promise<void>;
  loginRedirect: (returnTo?: string) => void;
  logout: () => void;
  isAuthenticated: boolean;
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

function decodeJwt<T = any>(token: string): T | null {
  try {
    const [, payload] = token.split('.');
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(decodeURIComponent(escape(json)));
  } catch {
    return null;
  }
}

function detectAuthMode(): AuthMode {
  const env = (process.env.REACT_APP_ENVIRONMENT || '').toLowerCase();
  const apiBase = (process.env.REACT_APP_API_BASE || '').toLowerCase();
  const hasCognito = !!process.env.REACT_APP_COGNITO_DOMAIN && !!process.env.REACT_APP_COGNITO_CLIENT_ID;
  const isLocalHost = window.location.hostname === 'localhost' || apiBase.includes('localhost');
  if (env === 'local') return 'local';
  if (hasCognito && !isLocalHost) return 'cognito';
  return 'local';
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const authMode = useMemo(detectAuthMode, []);

  useEffect(() => {
    // On mount, load tokens from storage
    const savedToken = localStorage.getItem('access_token');
    const savedIdToken = localStorage.getItem('id_token');
    setToken(savedToken);

    // If we have an id_token, derive a minimal user immediately for UI
    if (savedIdToken) {
      const claims = decodeJwt<any>(savedIdToken);
      if (claims) {
        setUser({
          id: -1,
          email: claims.email || claims['cognito:username'] || claims.sub || 'user',
          full_name: claims.name || null,
          is_superuser: false,
        });
      }
    }

    // Try to fetch the current user from backend (may refine user details)
    if (savedToken) {
      jerseyApi.getCurrentUser().then((u) => {
        setUser(u as User);
        localStorage.setItem('user', JSON.stringify(u));
      }).catch(() => {
        // ignore; UI can proceed with minimal user
      }).finally(() => setIsLoading(false));
    } else {
      setIsLoading(false);
    }
  }, []);

  const loginRedirect = (returnTo?: string) => {
    const domain = process.env.REACT_APP_COGNITO_DOMAIN;
    const clientId = process.env.REACT_APP_COGNITO_CLIENT_ID;
    const redirectUri = process.env.REACT_APP_COGNITO_REDIRECT_URI || `${window.location.origin}/auth/callback`;
    if (!domain || !clientId) {
      console.error('Cognito Hosted UI not configured. Set REACT_APP_COGNITO_DOMAIN and REACT_APP_COGNITO_CLIENT_ID.');
      return;
    }
    const currentPath = window.location.pathname + window.location.search;
    // Prefer explicit returnTo from caller (e.g., preserved from ProtectedRoute state)
    let dest = returnTo || currentPath;
    // Avoid redirecting back to /login; default to dashboard if that happens
    if (dest === '/login') dest = '/dashboard';
    // Persist for fallback in callback
    try { sessionStorage.setItem('returnTo', dest); } catch {}
    const state = encodeURIComponent(dest);
    const nonce = Math.random().toString(36).slice(2);
    // Use implicit flow requesting only the access token to avoid invalid_request
    const url = `${domain}/oauth2/authorize?response_type=token&client_id=${encodeURIComponent(clientId)}&redirect_uri=${encodeURIComponent(redirectUri)}&scope=${encodeURIComponent('openid email profile')}&state=${state}&nonce=${nonce}`;
    window.location.href = url;
  };

  const login = async (email: string, password: string) => {
    if (authMode === 'cognito') {
      // In non-local envs, redirect to Cognito
      loginRedirect();
      return;
    }
    // Local auth against backend
    const response = await jerseyApi.login(email, password);
    const { access_token } = response;
    setToken(access_token);
    localStorage.setItem('access_token', access_token);
    const userData = await jerseyApi.getCurrentUser();
    setUser(userData as User);
    localStorage.setItem('user', JSON.stringify(userData));
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('access_token');
    localStorage.removeItem('id_token');
    localStorage.removeItem('user');
    // Redirect to Cognito logout only in Cognito mode
    if (authMode === 'cognito') {
      const domain = process.env.REACT_APP_COGNITO_DOMAIN;
      const clientId = process.env.REACT_APP_COGNITO_CLIENT_ID;
      const postLogoutRedirect = process.env.REACT_APP_COGNITO_LOGOUT_REDIRECT_URI || window.location.origin;
      if (domain && clientId) {
        const logoutUrl = `${domain}/logout?client_id=${encodeURIComponent(clientId)}&logout_uri=${encodeURIComponent(postLogoutRedirect)}`;
        window.location.href = logoutUrl;
      }
    }
  };

  const value: AuthContextType = {
    user,
    token,
    authMode,
    login,
    loginRedirect,
    logout,
    isAuthenticated: !!token,
    isLoading,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
